import React, { useMemo } from 'react';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { Switch, Tag } from 'antd';
import { FiPlay } from 'react-icons/fi';
import { AiOutlineDelete } from 'react-icons/ai';
import { DataTable } from '@components/data-table';
import { TooltipTop } from '@components/tooltips';
import { InPageSpinner } from '@components/spinners/in-page-spinner';
import { formatDateTimeUtc, formatSchedule } from '../utils';
import { AirflowDag } from '../types';
import styles from '../styles.module.scss';

type DagTableProps = {
    dags: AirflowDag[];
    isLoading: boolean;
    sorting: SortingState;
    onSortingChange: (sorting: SortingState) => void;
    togglingDagId: string | null;
    onTogglePause: (dag: AirflowDag) => void;
    onTriggerRequest: (dagId: string) => void;
    onDeleteRequest: (dagId: string) => void;
    onRowClick: (dag: AirflowDag) => void;
};

const MAX_VISIBLE_TAGS = 3;

/*
 * Renders the DAGs list table with sortable columns, pause toggle, and row actions
 */
export function DagTable({
    dags,
    isLoading,
    sorting,
    onSortingChange,
    togglingDagId,
    onTogglePause,
    onTriggerRequest,
    onDeleteRequest,
    onRowClick,
}: DagTableProps) {
    const columns = useMemo<ColumnDef<AirflowDag>[]>(
        () => [
            {
                id: 'dag_id',
                header: 'DAG',
                enableSorting: true,
                size: 240,
                cell: ({ row }) => {
                    const dag = row.original;
                    const displayName = dag.dag_display_name ?? dag.dag_id;
                    return (
                        <div>
                            <div style={{ fontWeight: 600 }}>{displayName}</div>
                            {displayName !== dag.dag_id && <div style={{ fontSize: 12, color: '#666a8d' }}>{dag.dag_id}</div>}
                        </div>
                    );
                },
            },
            {
                id: 'owners',
                header: 'Owner',
                enableSorting: false,
                size: 90,
                cell: ({ row }) => row.original.owners?.join(', ') || '—',
            },
            {
                id: 'timetable_summary',
                header: 'Schedule',
                enableSorting: false,
                size: 120,
                cell: ({ row }) => formatSchedule(row.original.timetable_description, row.original.timetable_summary),
            },
            {
                id: 'tags',
                header: 'Tags',
                enableSorting: false,
                size: 120,
                cell: ({ row }) => {
                    const tags = row.original.tags ?? [];
                    const visible = tags.slice(0, MAX_VISIBLE_TAGS);
                    const overflow = tags.length - visible.length;
                    return (
                        <>
                            {visible.map((tag) => (
                                <Tag key={tag.name}>{tag.name}</Tag>
                            ))}
                            {overflow > 0 && <Tag>{`+${overflow}`}</Tag>}
                        </>
                    );
                },
            },
            {
                id: 'last-run',
                header: () => <span title="Last time this DAG's file was parsed">Last Run</span>,
                enableSorting: false,
                size: 165,
                cell: ({ row }) => formatDateTimeUtc(row.original.last_parsed_time),
            },
            {
                id: 'next_dagrun_logical_date',
                header: () => <span title="Next scheduled run">Next Run</span>,
                enableSorting: true,
                size: 165,
                cell: ({ row }) => formatDateTimeUtc(row.original.next_dagrun ?? row.original.next_dagrun_logical_date ?? row.original.next_dagrun_run_after),
            },
            {
                id: 'is_paused',
                header: 'Active',
                enableSorting: false,
                size: 72,
                cell: ({ row }) => {
                    const dag = row.original;
                    return (
                        <Switch
                            checked={!dag.is_paused}
                            disabled={togglingDagId === dag.dag_id}
                            onChange={() => onTogglePause(dag)}
                            onClick={(_checked, event) => event.stopPropagation()}
                        />
                    );
                },
            },
            {
                id: 'actions',
                header: 'Actions',
                enableSorting: false,
                size: 80,
                cell: ({ row }) => {
                    const dag = row.original;
                    return (
                        <span style={{ display: 'flex', gap: 12 }}>
                            <TooltipTop title="Trigger">
                                <span
                                    className={`${styles.actionIcon} ${dag.is_paused ? styles.disabled : ''}`}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        if (!dag.is_paused) onTriggerRequest(dag.dag_id);
                                    }}
                                >
                                    <FiPlay />
                                </span>
                            </TooltipTop>
                            <TooltipTop title="Delete">
                                <span
                                    className={`${styles.actionIcon} ${styles.destructive}`}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onDeleteRequest(dag.dag_id);
                                    }}
                                >
                                    <AiOutlineDelete />
                                </span>
                            </TooltipTop>
                        </span>
                    );
                },
            },
        ],
        [togglingDagId, onTogglePause, onTriggerRequest, onDeleteRequest]
    );

    if (isLoading && (dags ?? []).length === 0) {
        return <InPageSpinner size="Small" />;
    }

    return (
        <DataTable
            data={dags}
            columns={columns}
            sorting={sorting}
            onSortingChange={onSortingChange}
            onRowClick={onRowClick}
            rowKey={(dag) => dag.dag_id}
            isLoading={isLoading}
            emptyText="No DAGs found"
        />
    );
}
